import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, updateDoc, doc, query, where, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { fetchCompany } from "../utils/plan";
import { notifyOperator } from "../utils/mail";

// 利用者から運営（システム管理者）へのお問い合わせ。
// 送信側は Inquiry、運営側の一覧は InquiryAdmin。どちらも inquiries コレクションを見る。

const CATEGORIES = ["不具合の報告", "ご意見・ご感想", "使い方の質問", "その他"];

const CAT_COLOR = {
  "不具合の報告":   "#e74c3c",
  "ご意見・ご感想": "#27ae60",
  "使い方の質問":   "#2980b9",
  "その他":         "#7f8c8d",
};

const EMPTY_FORM = { category: "不具合の報告", subject: "", body: "" };

// この種別を選んだときは、まずAIアシスタントが答える
const HOWTO_CATEGORY = "使い方の質問";

const SAMPLE_QUESTIONS = [
  "打刻のQRコードはどこにありますか？",
  "有給申請はどうやって出しますか？",
  "社員を追加するには？",
  "会社コードはどこで確認できますか？",
];

const fmt = (ts) => ts?.toDate().toLocaleString("ja-JP", {
  year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
});

// ============================================================
// 利用者側：送信フォーム＋自分の履歴
// ============================================================
function Inquiry({ user, companyId }) {
  const [form, setForm]     = useState(EMPTY_FORM);
  const [mine, setMine]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);
  const [error, setError]   = useState("");
  // AIアシスタント用
  const [chat, setChat]           = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");
  const [escalated, setEscalated] = useState(false);
  const chatEndRef = useRef(null);
  const functions = getFunctions(undefined, "us-central1");

  const isHowTo   = form.category === HOWTO_CATEGORY;
  const showChat  = isHowTo && !escalated;

  useEffect(() => { fetchMine(); }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ block: "end" });
  }, [chat, chatLoading]);

  // AIアシスタントへ質問を送る
  const askAI = async (text) => {
    const q = (text || "").trim();
    if (!q || chatLoading) return;
    const next = [...chat, { role: "user", text: q }];
    setChat(next);
    setChatInput("");
    setChatError("");
    setChatLoading(true);
    try {
      const call = httpsCallable(functions, "askAssistant");
      const res = await call({ messages: next.map(m => ({ role: m.role, text: m.text })) });
      setChat([...next, { role: "model", text: res.data.text }]);
    } catch (e) {
      console.error("AIアシスタントの応答に失敗しました:", e);
      setChatError("回答を取得できませんでした。時間をおいて試すか、下のボタンから運営へお問い合わせください。");
    } finally {
      setChatLoading(false);
    }
  };

  // 解決しなかったので運営へ。やりとりを問い合わせ内容に引き継ぐ。
  const escalate = () => {
    const firstQ = chat.find(m => m.role === "user");
    const transcript = chat.map(m => `${m.role === "user" ? "【質問】" : "【AIの回答】"}\n${m.text}`).join("\n\n");
    setForm(f => ({
      ...f,
      subject: f.subject || (firstQ ? firstQ.text.slice(0, 60) : ""),
      body: f.body || (transcript ? `AIアシスタントとのやりとり:\n\n${transcript}\n\n---\n（補足があればご記入ください）` : ""),
    }));
    setEscalated(true);
  };

  const fetchMine = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "inquiries"), where("uid", "==", user.uid)));
      setMine(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt));
    } catch (e) {
      console.error("お問い合わせ履歴の取得に失敗しました:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSent(false);
    setSending(true);
    try {
      // 運営が会社を特定しやすいよう、会社名も一緒に残す
      let companyName = "";
      try {
        const company = await fetchCompany(companyId);
        companyName = company?.name || "";
      } catch { /* 会社名が取れなくても送信は続ける */ }

      await addDoc(collection(db, "inquiries"), {
        uid: user.uid,
        email: user.email,
        companyId: companyId || "",
        companyName,
        category: form.category,
        subject: form.subject,
        body: form.body,
        status: "open",
        reply: "",
        repliedAt: null,
        createdAt: Timestamp.now(),
      });

      // 運営が気づけるようメールでも知らせる。送れなくても送信は成立させる。
      await notifyOperator({
        event_type:   "お問い合わせ",
        company_name: companyName ? `${companyName}（${companyId || "—"}）` : (companyId || "（会社未所属）"),
        user_name:    user.email,
        user_email:   user.email,
        detail:       `種別：${form.category}\n件名：${form.subject}\n\n${form.body}`,
      });

      setForm(EMPTY_FORM);
      setSent(true);
      fetchMine();
    } catch (err) {
      console.error("お問い合わせの送信に失敗しました:", err);
      setError("送信に失敗しました。時間をおいてもう一度お試しください。");
    } finally {
      setSending(false);
    }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div style={st.container}>
      <h2 style={st.title}>✉️ お問い合わせ</h2>
      <p style={st.lead}>
        不具合のご報告、ご意見・ご感想、使い方のご質問などをお送りください。運営が確認し、必要に応じて返信します。
      </p>

      <div style={st.form}>
        <label style={st.label}>種別</label>
        <div style={st.catGrid}>
          {CATEGORIES.map(c => (
            <button key={c} type="button"
              style={{ ...st.catBtn, ...(form.category === c ? { backgroundColor: CAT_COLOR[c], border: `2px solid ${CAT_COLOR[c]}`, color: "#fff" } : {}) }}
              onClick={() => { set("category", c); setEscalated(false); }}
            >{c}</button>
          ))}
        </div>

        {showChat ? (
          <div>
            <div style={st.chatLog}>
              {chat.length === 0 && (
                <div style={st.chatIntro}>
                  <p style={{ margin: "0 0 12px" }}>使い方について、AIアシスタントがその場でお答えします。下の例を押すか、自由に入力してください。</p>
                  <div style={st.chipRow}>
                    {SAMPLE_QUESTIONS.map(q => (
                      <button key={q} type="button" style={st.chip} onClick={() => askAI(q)}>{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {chat.map((m, i) => (
                <div key={i} style={m.role === "user" ? st.rowUser : st.rowAi}>
                  <div style={m.role === "user" ? st.bubbleUser : st.bubbleAi}>{m.text}</div>
                </div>
              ))}
              {chatLoading && (
                <div style={st.rowAi}><div style={{ ...st.bubbleAi, color: "#999" }}>考えています…</div></div>
              )}
              <div ref={chatEndRef} />
            </div>

            {chatError && <p style={st.error}>{chatError}</p>}

            <div style={st.chatInputRow}>
              <input style={{ ...st.input, marginBottom: 0, flex: 1 }} type="text" value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); askAI(chatInput); } }}
                placeholder="質問を入力してください" disabled={chatLoading} />
              <button type="button" style={st.sendBtn} onClick={() => askAI(chatInput)} disabled={chatLoading}>送信</button>
            </div>

            <p style={st.aiNote}>※ AIの回答が正しくないことがあります。料金・契約・データの内容に関することは運営へお問い合わせください。</p>

            <button type="button" style={st.escalateBtn} onClick={escalate}>
              解決しない場合は運営に問い合わせる →
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {isHowTo && escalated && (
              <p style={st.backNote}>
                AIで解決しなかった内容を運営へ送ります。
                <button type="button" style={st.backBtn} onClick={() => setEscalated(false)}>← AIに聞き直す</button>
              </p>
            )}

            <label style={st.label}>件名</label>
            <input style={st.input} type="text" value={form.subject}
              onChange={e => set("subject", e.target.value)}
              placeholder="例：打刻のQRコードが読み取れない" required />

            <label style={st.label}>内容</label>
            <textarea style={{ ...st.input, height: "160px", resize: "vertical" }} value={form.body}
              onChange={e => set("body", e.target.value)}
              placeholder="できるだけ具体的にご記入ください。不具合の場合は、どの画面で何をしたときに起きたかを書いていただけると助かります。" required />

            {error && <p style={st.error}>{error}</p>}
            {sent  && <p style={st.sentMsg}>✅ 送信しました。ありがとうございます。</p>}

            <button style={st.submitBtn} type="submit" disabled={sending}>
              {sending ? "送信中..." : "送信する"}
            </button>
          </form>
        )}
      </div>

      <h3 style={st.subTitle}>これまでのお問い合わせ</h3>
      {loading
        ? <p style={st.empty}>読み込み中...</p>
        : mine.length === 0
          ? <p style={st.empty}>まだお問い合わせはありません</p>
          : mine.map(q => (
            <div key={q.id} style={{ ...st.card, borderLeft: `4px solid ${CAT_COLOR[q.category] || "#7f8c8d"}` }}>
              <div style={st.cardTop}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <span style={{ ...st.catBadge, backgroundColor: CAT_COLOR[q.category] || "#7f8c8d" }}>{q.category}</span>
                  <h3 style={st.itemTitle}>{q.subject}</h3>
                </div>
                <span style={{ ...st.statusBadge, ...(q.status === "done" ? st.statusDone : st.statusOpen) }}>
                  {q.status === "done" ? "対応済み" : "確認中"}
                </span>
              </div>
              <p style={st.body}>{q.body}</p>
              {q.reply && (
                <div style={st.replyBox}>
                  <div style={st.replyLabel}>運営からの返信</div>
                  <p style={{ ...st.body, margin: 0 }}>{q.reply}</p>
                </div>
              )}
              <div style={st.meta}>送信日時：{fmt(q.createdAt)}</div>
            </div>
          ))
      }
    </div>
  );
}

// ============================================================
// 運営側：全社からのお問い合わせ一覧と返信
// ============================================================
function InquiryAdmin({ onCountChange }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("open");
  const [drafts, setDrafts]   = useState({});
  const [working, setWorking] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "inquiries"));
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt);
      setItems(rows);
      if (onCountChange) onCountChange(rows.filter(r => r.status !== "done").length);
    } catch (e) {
      console.error("お問い合わせの取得に失敗しました:", e);
    } finally {
      setLoading(false);
    }
  };

  const save = async (id, { reply, status }) => {
    setWorking(id);
    try {
      const patch = {};
      if (reply !== undefined) { patch.reply = reply; patch.repliedAt = Timestamp.now(); }
      if (status !== undefined) patch.status = status;
      await updateDoc(doc(db, "inquiries", id), patch);
      await fetchAll();
    } catch (e) {
      console.error("お問い合わせの更新に失敗しました:", e);
      alert("更新に失敗しました。");
    } finally {
      setWorking(null);
    }
  };

  const shown = filter === "open" ? items.filter(q => q.status !== "done") : items;
  const openCount = items.filter(q => q.status !== "done").length;

  return (
    <div style={st.container}>
      <h2 style={st.title}>✉️ お問い合わせ</h2>
      <p style={st.lead}>全社の利用者から届いたお問い合わせです。未対応：{openCount}件</p>

      <div style={st.filterRow}>
        <button type="button" style={{ ...st.filterBtn, ...(filter === "open" ? st.filterActive : {}) }}
          onClick={() => setFilter("open")}>未対応のみ</button>
        <button type="button" style={{ ...st.filterBtn, ...(filter === "all" ? st.filterActive : {}) }}
          onClick={() => setFilter("all")}>すべて</button>
      </div>

      {loading
        ? <p style={st.empty}>読み込み中...</p>
        : shown.length === 0
          ? <p style={st.empty}>{filter === "open" ? "未対応のお問い合わせはありません" : "お問い合わせはありません"}</p>
          : shown.map(q => (
            <div key={q.id} style={{ ...st.card, borderLeft: `4px solid ${CAT_COLOR[q.category] || "#7f8c8d"}` }}>
              <div style={st.cardTop}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <span style={{ ...st.catBadge, backgroundColor: CAT_COLOR[q.category] || "#7f8c8d" }}>{q.category}</span>
                  <h3 style={st.itemTitle}>{q.subject}</h3>
                </div>
                <span style={{ ...st.statusBadge, ...(q.status === "done" ? st.statusDone : st.statusOpen) }}>
                  {q.status === "done" ? "対応済み" : "未対応"}
                </span>
              </div>

              <div style={st.fromRow}>
                {q.companyName ? `${q.companyName}（${q.companyId}）` : q.companyId || "会社未設定"} · {q.email}
              </div>
              <p style={st.body}>{q.body}</p>
              <div style={st.meta}>受信日時：{fmt(q.createdAt)}{q.repliedAt ? ` / 返信：${fmt(q.repliedAt)}` : ""}</div>

              <div style={st.replyEditor}>
                <label style={st.label}>返信（利用者の画面に表示されます）</label>
                <textarea style={{ ...st.input, height: "100px", resize: "vertical" }}
                  value={drafts[q.id] !== undefined ? drafts[q.id] : (q.reply || "")}
                  onChange={e => setDrafts(d => ({ ...d, [q.id]: e.target.value }))}
                  placeholder="返信内容を入力してください" />
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button type="button" style={st.replyBtn} disabled={working === q.id}
                    onClick={() => save(q.id, { reply: drafts[q.id] !== undefined ? drafts[q.id] : (q.reply || ""), status: "done" })}>
                    返信して対応済みにする
                  </button>
                  <button type="button" style={st.saveDraftBtn} disabled={working === q.id}
                    onClick={() => save(q.id, { reply: drafts[q.id] !== undefined ? drafts[q.id] : (q.reply || "") })}>
                    返信だけ保存
                  </button>
                  {q.status === "done"
                    ? <button type="button" style={st.reopenBtn} disabled={working === q.id}
                        onClick={() => save(q.id, { status: "open" })}>未対応に戻す</button>
                    : <button type="button" style={st.doneBtn} disabled={working === q.id}
                        onClick={() => save(q.id, { status: "done" })}>返信せず対応済みにする</button>}
                </div>
              </div>
            </div>
          ))
      }
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "6px" },
  lead: { color: "#7f8c8d", fontSize: "14px", lineHeight: "1.7", marginTop: 0, marginBottom: "24px" },
  subTitle: { color: "#2c3e50", fontSize: "17px", margin: "36px 0 16px" },
  form: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
  label: { display: "block", marginBottom: "8px", color: "#2c3e50", fontSize: "14px", fontWeight: "bold" },
  input: { width: "100%", padding: "12px", marginBottom: "16px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  catGrid: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" },
  catBtn: { padding: "8px 16px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d", fontWeight: "500" },
  submitBtn: { padding: "12px 32px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "12px" },
  sentMsg: { color: "#27ae60", fontSize: "14px", marginBottom: "12px" },
  empty: { color: "#999", textAlign: "center", marginTop: "32px" },
  card: { backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", marginBottom: "8px" },
  catBadge: { padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold", color: "#fff", whiteSpace: "nowrap" },
  itemTitle: { color: "#2c3e50", margin: 0, fontSize: "16px" },
  statusBadge: { padding: "4px 12px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold", whiteSpace: "nowrap" },
  statusOpen: { backgroundColor: "#f39c1222", color: "#f39c12" },
  statusDone: { backgroundColor: "#27ae6022", color: "#27ae60" },
  fromRow: { fontSize: "13px", color: "#7f8c8d", marginBottom: "8px" },
  body: { color: "#555", fontSize: "14px", lineHeight: "1.8", whiteSpace: "pre-wrap", margin: "0 0 10px" },
  meta: { fontSize: "12px", color: "#bbb" },
  replyBox: { backgroundColor: "#f0f8f4", border: "1px solid #c3e6cb", borderRadius: "8px", padding: "14px 16px", marginBottom: "10px" },
  replyLabel: { fontSize: "12px", fontWeight: "bold", color: "#27ae60", marginBottom: "6px" },
  replyEditor: { marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #eee" },
  replyBtn: { padding: "10px 20px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  saveDraftBtn: { padding: "10px 20px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #ccd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  doneBtn: { padding: "10px 20px", backgroundColor: "#fff", color: "#7f8c8d", border: "1px solid #ccd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  reopenBtn: { padding: "10px 20px", backgroundColor: "#fff", color: "#e67e22", border: "1px solid #e67e22", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  filterRow: { display: "flex", gap: "8px", marginBottom: "20px" },
  filterBtn: { padding: "6px 16px", border: "1px solid #ddd", borderRadius: "20px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  filterActive: { backgroundColor: "#2c3e50", border: "1px solid #2c3e50", color: "#fff" },
  // AIアシスタント
  chatLog: { border: "1px solid #e6ebf0", borderRadius: "10px", backgroundColor: "#fafbfc", padding: "16px", height: "320px", overflowY: "auto", marginBottom: "12px" },
  chatIntro: { color: "#7f8c8d", fontSize: "14px", lineHeight: "1.7" },
  chipRow: { display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" },
  chip: { padding: "8px 14px", borderRadius: "20px", border: "1px solid #ccd", backgroundColor: "#fff", color: "#2980b9", cursor: "pointer", fontSize: "13px", textAlign: "left" },
  rowUser: { display: "flex", justifyContent: "flex-end", marginBottom: "10px" },
  rowAi:   { display: "flex", justifyContent: "flex-start", marginBottom: "10px" },
  bubbleUser: { maxWidth: "80%", padding: "10px 14px", borderRadius: "14px 14px 2px 14px", backgroundColor: "#2c3e50", color: "#fff", fontSize: "14px", lineHeight: "1.7", whiteSpace: "pre-wrap" },
  bubbleAi:   { maxWidth: "85%", padding: "10px 14px", borderRadius: "14px 14px 14px 2px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #e6ebf0", fontSize: "14px", lineHeight: "1.7", whiteSpace: "pre-wrap" },
  chatInputRow: { display: "flex", gap: "8px", alignItems: "center", marginBottom: "10px" },
  sendBtn: { padding: "12px 24px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px", flexShrink: 0 },
  aiNote: { fontSize: "12px", color: "#95a5a6", lineHeight: "1.7", margin: "0 0 16px" },
  escalateBtn: { padding: "10px 20px", backgroundColor: "#fff", color: "#2980b9", border: "1px solid #2980b9", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  backNote: { fontSize: "13px", color: "#7f8c8d", marginTop: 0, marginBottom: "16px" },
  backBtn: { marginLeft: "8px", background: "none", border: "none", color: "#2980b9", cursor: "pointer", fontSize: "13px", padding: 0 },
};

export { InquiryAdmin };
export default Inquiry;
